import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, PackageOpen } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  available: "bg-emerald-500",
  assigned: "bg-amber-500",
  maintenance: "bg-orange-500",
  retired: "bg-zinc-500",
};

const GADGETS = ["phone", "laptop", "tablet", "smartwatch", "headphones", "camera", "gaming", "tv", "other"];

export default function AdminLoaners() {
  const [data, setData] = useState({ rows: [], counts: {} });
  const [filter, setFilter] = useState({ status: "all", country_id: "all" });
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ brand: "", model: "", serial: "", gadget_category: "phone", condition: "good", country_id: "UK", notes: "" });

  const load = useCallback(async () => {
    const params = {};
    if (filter.status !== "all") params.status = filter.status;
    if (filter.country_id !== "all") params.country_id = filter.country_id;
    const r = await api.get("/admin/loaners", { params });
    setData(r.data);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.brand || !form.model) { toast.error("Brand + model required"); return; }
    try {
      await api.post("/admin/loaners", form);
      toast.success("Loaner added to inventory");
      setOpenNew(false);
      setForm({ brand: "", model: "", serial: "", gadget_category: "phone", condition: "good", country_id: "UK", notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const retire = async (id) => {
    if (!window.confirm("Retire this loaner? It won't appear in future auto-assigns.")) return;
    try {
      await api.delete(`/admin/loaners/${id}`);
      toast.success("Retired");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Retire failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-loaners-page">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
          <PackageOpen className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1">
          <h1 className="text-3xl font-display font-bold">Loaner devices</h1>
          <p className="text-sm text-muted-foreground">Vault + Household perk. Auto-assigned by country + gadget category.</p>
        </div>
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button data-testid="loaner-new-btn"><Plus className="w-4 h-4 mr-1" /> New loaner</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add loaner to inventory</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Brand</Label><Input value={form.brand} onChange={e => setForm({ ...form, brand: e.target.value })} data-testid="loaner-brand-input" /></div>
              <div><Label>Model</Label><Input value={form.model} onChange={e => setForm({ ...form, model: e.target.value })} data-testid="loaner-model-input" /></div>
              <div><Label>Serial / IMEI</Label><Input value={form.serial} onChange={e => setForm({ ...form, serial: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Gadget category</Label>
                  <Select value={form.gadget_category} onValueChange={v => setForm({ ...form, gadget_category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{GADGETS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Country</Label>
                  <Select value={form.country_id} onValueChange={v => setForm({ ...form, country_id: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="UK">UK</SelectItem><SelectItem value="MY">Malaysia</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button onClick={create} data-testid="loaner-create-confirm">Add</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(data.counts).map(([k, v]) => (
          <Card key={k}>
            <CardContent className="pt-6">
              <div className="text-xs uppercase text-muted-foreground">{k}</div>
              <div className="text-3xl font-bold" data-testid={`loaner-count-${k}`}>{v}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle>Inventory</CardTitle>
          <div className="flex gap-2">
            <Select value={filter.status} onValueChange={v => setFilter({ ...filter, status: v })}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="maintenance">Maintenance</SelectItem>
                <SelectItem value="retired">Retired</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filter.country_id} onValueChange={v => setFilter({ ...filter, country_id: v })}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Both</SelectItem>
                <SelectItem value="UK">UK</SelectItem>
                <SelectItem value="MY">Malaysia</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {data.rows.length === 0 ? (
            <div className="text-sm text-muted-foreground">No loaners in inventory.</div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-xs text-muted-foreground text-left"><th className="p-2">Device</th><th>Serial</th><th>Category</th><th>Country</th><th>Status</th><th>Assigned to</th><th></th></tr></thead>
              <tbody>
                {data.rows.map(l => (
                  <tr key={l.id} className="border-t border-border" data-testid={`loaner-row-${l.id}`}>
                    <td className="p-2 font-medium">{l.brand} {l.model}</td>
                    <td className="text-xs font-mono">{l.serial || "—"}</td>
                    <td className="text-xs capitalize">{l.gadget_category}</td>
                    <td className="text-xs">{l.country_id}</td>
                    <td><Badge className={`${STATUS_COLORS[l.status]} text-white`}>{l.status}</Badge></td>
                    <td className="text-xs">{l.assigned_claim_id ? `Claim ${l.assigned_claim_id.slice(0, 8)}` : "—"}</td>
                    <td>
                      {l.status !== "assigned" && l.status !== "retired" && (
                        <Button size="sm" variant="ghost" onClick={() => retire(l.id)} data-testid={`retire-${l.id}`}>Retire</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
